import { useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  type TableOptions,
  type GroupingState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import type { ListViewOptions } from '../types';

export interface UseListViewReturn<T> {
  table: ReturnType<typeof useReactTable<T>>;
  grouping: GroupingState;
  sorting: SortingState;
  columnVisibility: VisibilityState;
  setGrouping: (grouping: GroupingState) => void;
  setSorting: (sorting: SortingState) => void;
  setColumnVisibility: (visibility: VisibilityState) => void;
}

export function useListView<T>(data: T[], options: ListViewOptions<T>): UseListViewReturn<T> {
  const {
    columns,
    getRowId,
    defaultGrouping = [],
    defaultSorting = [],
    defaultVisibility = {},
    meta,
    sorting: controlledSorting,
    onSortingChange: externalOnSortingChange,
    manualSorting = false,
  } = options;

  const [grouping, setGroupingState] = useState<GroupingState>(defaultGrouping);
  const [internalSorting, setInternalSortingState] = useState<SortingState>(defaultSorting);
  const [columnVisibility, setColumnVisibilityState] = useState<VisibilityState>(defaultVisibility);

  const isControlledSorting = controlledSorting !== undefined;
  const currentSorting = isControlledSorting ? controlledSorting : internalSorting;

  const handleSortingChange = (updater: SortingState | ((prev: SortingState) => SortingState)) => {
    const newSorting = typeof updater === 'function' ? updater(currentSorting) : updater;
    if (!isControlledSorting) setInternalSortingState(newSorting);
    externalOnSortingChange?.(newSorting);
  };

  const tableOptions: TableOptions<T> = {
    data,
    columns,
    state: {
      grouping,
      sorting: currentSorting,
      columnVisibility,
    },
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowId: getRowId ? (originalRow) => getRowId(originalRow as T) : undefined,
    enableGrouping: true,
    enableSorting: true,
    enableMultiSort: true,
    manualSorting,
    columnResizeMode: 'onChange',
    meta,
  };

  const table = useReactTable(tableOptions);

  const setGrouping = (newGrouping: GroupingState) => {
    setGroupingState(newGrouping);
  };

  const setSorting = (newSorting: SortingState) => {
    handleSortingChange(newSorting);
  };

  const setColumnVisibility = (newVisibility: VisibilityState) => {
    setColumnVisibilityState(newVisibility);
  };

  return {
    table,
    grouping: table.getState().grouping,
    sorting: table.getState().sorting,
    columnVisibility: table.getState().columnVisibility,
    setGrouping,
    setSorting,
    setColumnVisibility,
  };
}
